import { useEffect } from 'react';

// Web Vitals measurement and reporting
interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

type GtagFn = (...args: unknown[]) => void;
type LayoutShiftEntry = PerformanceEntry & { value: number; hadRecentInput: boolean };
type FirstInputEntry = PerformanceEntry & { processingStart: number };
type NavigationEntry = PerformanceEntry & { responseStart: number };
type ResourceEntry = PerformanceEntry & { transferSize?: number };

// Thresholds for Core Web Vitals
const THRESHOLDS = {
  FCP: { good: 1800, poor: 3000 },
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  TTFB: { good: 800, poor: 1800 }
};

const getRating = (name: keyof typeof THRESHOLDS, value: number): 'good' | 'needs-improvement' | 'poor' => {
  const threshold = THRESHOLDS[name];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
};

const reportMetric = (metric: WebVitalsMetric) => {
  // Report to analytics (Google Analytics, custom analytics, etc.)
  const gtag = (typeof window !== 'undefined' ? (window as unknown as { gtag?: GtagFn }).gtag : undefined);
  if (gtag) {
    gtag('event', metric.name, {
      event_category: 'Web Vitals',
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      event_label: metric.id,
      non_interaction: true,
    });
  }

  // Console logging for development
  if (import.meta.env.DEV) {
    console.group(`🚀 Web Vitals: ${metric.name}`);
    console.log(`Value: ${metric.value}ms (${metric.rating})`);
    console.log(`ID: ${metric.id}`);
    console.log(`Delta: ${metric.delta}ms`);
    console.groupEnd();
  }
};

export const WebVitalsTracker = () => {
  useEffect(() => {
    // Track First Contentful Paint (FCP)
    const observeFCP = () => {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.name === 'first-contentful-paint') {
            const metric: WebVitalsMetric = {
              name: 'FCP',
              value: entry.startTime,
              rating: getRating('FCP', entry.startTime),
              delta: entry.startTime,
              id: `${Date.now()}-${Math.random()}`
            };
            reportMetric(metric);
          }
        });
      });
      
      try {
        observer.observe({ entryTypes: ['paint'] });
      } catch (e) {
        // Paint timing not supported
      }
    };

    // Track Largest Contentful Paint (LCP)
    const observeLCP = () => {
      let largestContentfulPaint = 0;
      
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        if (lastEntry && lastEntry.startTime > largestContentfulPaint) {
          largestContentfulPaint = lastEntry.startTime;
          
          const metric: WebVitalsMetric = {
            name: 'LCP',
            value: largestContentfulPaint,
            rating: getRating('LCP', largestContentfulPaint),
            delta: largestContentfulPaint,
            id: `${Date.now()}-${Math.random()}`
          };
          reportMetric(metric);
        }
      });

      try {
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // LCP not supported
      }
    };

    // Track First Input Delay (FID)
    const observeFID = () => {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((raw) => {
          const entry = raw as FirstInputEntry;
          const metric: WebVitalsMetric = {
            name: 'FID',
            value: entry.processingStart - entry.startTime,
            rating: getRating('FID', entry.processingStart - entry.startTime),
            delta: entry.processingStart - entry.startTime,
            id: `${Date.now()}-${Math.random()}`
          };
          reportMetric(metric);
        });
      });

      try {
        observer.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // FID not supported
      }
    };

    // Track Cumulative Layout Shift (CLS)
    const observeCLS = () => {
      let clsValue = 0;
      let sessionValue = 0;
      let sessionEntries: LayoutShiftEntry[] = [];

      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();

        entries.forEach((raw) => {
          const entry = raw as LayoutShiftEntry;
          if (!entry.hadRecentInput) {
            const firstSessionEntry = sessionEntries[0];
            const lastSessionEntry = sessionEntries[sessionEntries.length - 1];

            if (sessionValue && 
                entry.startTime - lastSessionEntry.startTime < 1000 &&
                entry.startTime - firstSessionEntry.startTime < 5000) {
              sessionValue += entry.value;
              sessionEntries.push(entry);
            } else {
              sessionValue = entry.value;
              sessionEntries = [entry];
            }

            if (sessionValue > clsValue) {
              clsValue = sessionValue;
              
              const metric: WebVitalsMetric = {
                name: 'CLS',
                value: clsValue,
                rating: getRating('CLS', clsValue),
                delta: entry.value,
                id: `${Date.now()}-${Math.random()}`
              };
              reportMetric(metric);
            }
          }
        });
      });

      try {
        observer.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // CLS not supported
      }
    };

    // Track Time to First Byte (TTFB)
    const observeTTFB = () => {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((raw) => {
          const entry = raw as NavigationEntry;
          if (entry.responseStart > 0) {
            const metric: WebVitalsMetric = {
              name: 'TTFB',
              value: entry.responseStart,
              rating: getRating('TTFB', entry.responseStart),
              delta: entry.responseStart,
              id: `${Date.now()}-${Math.random()}`
            };
            reportMetric(metric);
          }
        });
      });

      try {
        observer.observe({ entryTypes: ['navigation'] });
      } catch (e) {
        // Navigation timing not supported
      }
    };

    // Initialize all observers
    observeFCP();
    observeLCP();
    observeFID();
    observeCLS();
    observeTTFB();

    // Track custom metrics
    const trackCustomMetrics = () => {
      // Time to Interactive (TTI) approximation
      setTimeout(() => {
        if (performance.timing) {
          const tti = performance.timing.domInteractive - performance.timing.navigationStart;
          console.log(`⚡ TTI: ${tti}ms`);
        }
      }, 0);

      // Bundle size tracking
      if (performance.getEntriesByType) {
        const resources = performance.getEntriesByType('resource');
        let totalSize = 0;
        resources.forEach((raw) => {
          const resource = raw as ResourceEntry;
          if (resource.transferSize) {
            totalSize += resource.transferSize;
          }
        });
        console.log(`📦 Total transferred: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);
      }
    };

    trackCustomMetrics();

    // Cleanup function
    return () => {
      // Observers are automatically cleaned up when component unmounts
    };
  }, []);

  return null;
};

export default WebVitalsTracker;